import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';
import FooterLayout from '../components/layout/Footer';

const {
    FiLibrary, FiSearch, FiExternalLink,
    FiEdit3, FiFolder, FiCopy, FiShare2, FiPlus, FiTrash2, FiCheck, FiX, FiEdit,
    FiHeart, FiUpload, FiArrowLeft
} = FiIcons;

const Books = () => {
    const {
        user, isAdmin, addFavorite, addNote, addCategory, addItemToCategory, books, fetchBooks,
        addCustomBook, updateCustomBook, deleteBook, updateBookProgress
    } = useAuth();

    useEffect(() => {
        fetchBooks();
    }, []);

    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Interactive States
    const [activeNoteBook, setActiveNoteBook] = useState(null);
    const [noteContent, setNoteContent] = useState('');
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [activeCategoryBook, setActiveCategoryBook] = useState(null);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [viewingBook, setViewingBook] = useState(null); // State for Single Book View

    // Add/Edit Book Modal State
    const [showAddBookModal, setShowAddBookModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingBookId, setEditingBookId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [newBook, setNewBook] = useState({
        title: '',
        category: 'quran',
        author: '',
        description: '',
        link: '',
        cover: '',
        is_local: false,
        format: 'External'
    });

    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    
    // Sample categories (Static Definitions)
    const categories = [
        { id: 'all', name: 'All Books' },
        { id: 'quran', name: 'Quran Studies' },
        { id: 'hadith', name: 'Hadith Studies' },
        { id: 'fiqh', name: 'Islamic Jurisprudence' },
        { id: 'biography', name: 'Biography' },
        { id: 'history', name: 'Islamic History' },
        { id: 'spirituality', name: 'Spirituality' }
    ];

    // Sample books data
    // const sampleBooks = [
    //     {
    //         id: 1,
    //         title: 'The Noble Quran',
    //         author: 'Dr. Muhammad Taqi-ud-Din Al-Hilali',
    //         category: 'quran',
    //         format: 'PDF',
    //         cover: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=300&h=400&fit=crop',
    //         description: 'English translation and commentary of the Holy Quran with detailed explanations.',
    //         amazonLink: 'https://amazon.com/noble-quran',
    //         downloadUrl: '/books/noble-quran.pdf',
    //         is_local: true
    //     },
    //     {
    //         id: 2,
    //         title: 'Sahih Al-Bukhari',
    //         author: 'Imam Bukhari',
    //         category: 'hadith',
    //         format: 'PDF',
    //         cover: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=300&h=400&fit=crop',
    //         description: 'Complete collection of authentic Hadith compiled by Imam Bukhari.',
    //         amazonLink: 'https://amazon.com/sahih-bukhari',
    //         downloadUrl: '/books/sahih-bukhari.pdf',
    //         is_local: true
    //     },
    //     {
    //         id: 3,
    //         title: 'The Sealed Nectar',
    //         author: 'Safi-ur-Rahman al-Mubarakpuri',
    //         category: 'biography',
    //         format: 'External',
    //         cover: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=400&fit=crop',
    //         description: 'Biography of Prophet Muhammad (PBUH) - Winner of the World Muslim League Prize.',
    //         amazonLink: 'https://amazon.com/sealed-nectar',
    //         is_local: false
    //     },
    //     {
    //         id: 4,
    //         title: 'Fortress of the Muslim',
    //         author: 'Sa\'id ibn Wahf Al-Qahtani',
    //         category: 'spirituality',
    //         format: 'PDF',
    //         cover: 'https://images.unsplash.com/photo-1519904981063-b0cf448d479e?w=300&h=400&fit=crop',
    //         description: 'Collection of authentic duas and dhikr for daily protection and remembrance.',
    //         amazonLink: 'https://amazon.com/fortress-muslim',
    //         downloadUrl: '/books/fortress-muslim.pdf',
    //         is_local: true
    //     },
    //     {
    //         id: 5,
    //         title: 'Islamic Jurisprudence',
    //         author: 'Dr. Abdur Rahman I. Doi',
    //         category: 'fiqh',
    //         format: 'PDF',
    //         cover: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?w=300&h=400&fit=crop',
    //         description: 'Comprehensive guide to Islamic law and jurisprudence principles.',
    //         amazonLink: 'https://amazon.com/islamic-jurisprudence',
    //         downloadUrl: '/books/islamic-jurisprudence.pdf',
    //         is_local: true
    //     },
    //     {
    //         id: 6,
    //         title: 'The History of Islam',
    //         author: 'Akbar Shah Najeebabadi',
    //         category: 'history',
    //         format: 'External',
    //         cover: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=300&h=400&fit=crop',
    //         description: 'Comprehensive history of Islam from the time of Prophet Muhammad to modern times.',
    //         amazonLink: 'https://amazon.com/history-islam',
    //         is_local: false
    //     }
    // ];
    

    // Combine static and custom books, filtering out deleted ones
    const allBooks = useMemo(() => {
        // Guest users only see public books
        if (!user) {
            return books;
        }

        // Logged-in users see everything
        return [
            ...books.filter(b => !user.hiddenBooks?.includes(b.id)),
            ...(user.customBooks || [])
        ];
    }, [books, user]);

    const getCategoryCount = (catId) => {
        if (catId === 'all') return allBooks.length;
        return allBooks.filter(b => b.category === catId).length;
    };

    const filteredBooks = allBooks.filter(book => {
        let matchesCategory = false;

        // Check for user-created category first
        const userCategory = user?.categories?.find(c => c.id.toString() === selectedCategory);

        if (selectedCategory === 'all') {
            matchesCategory = true;
        } else if (userCategory) {
            const targetId = `book-${book.id}`;
            matchesCategory = (userCategory.items || []).some(
                item => item.id === targetId
            );
        } else {
            matchesCategory = book.category === selectedCategory;
        }

        const matchesSearch = book.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            book.author?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    // --- Actions ---
    const handleFavorite = (book) => {
        if (!user) return toast.error('Please sign in');
        addFavorite({
            id: `book-${book.title}`,
            type: 'book',
            title: book.title,
            text: book.description,
            translation: book.author,
            reference: book.category,
            date: new Date().toISOString()
        });
        toast.success('Added to favorites');
    };

    const handleSaveNote = () => {
        if (!user) return toast.error('Please sign in');
        if (!noteContent.trim()) return;
        addNote({
            id: Date.now(),
            type: 'book',
            reference: activeNoteBook.title,
            content: noteContent,
            date: new Date().toISOString()
        });
        setActiveNoteBook(null);
        setNoteContent('');
    };

    const handleCopy = (book) => {
        const text = `Title: ${book.title}\nAuthor: ${book.author}\nDescription: ${book.description}\nLink: ${book.link}`;
        navigator.clipboard.writeText(text);
        toast.success('Book info copied');
    };

    const handleShare = (book) => {
        const text = `Check out this book: ${book.title} by ${book.author}`;
        const url = book.link || window.location.href;
        const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        window.open(shareUrl, '_blank');
    };

    const handleAddToCategory = async () => {
        if (!user) return toast.error('Please sign in');
        if (!activeCategoryBook || !newCategoryName.trim()) return;
        try {
            const response = await addCategory(newCategoryName);
            const newCategoryId = response.data.id; // assuming API returns { id: ..., name: ... }

            // 3️⃣ Call your existing function to save Hadith to this new category
            handleSaveToExistingCategory(newCategoryId);

            // 4️⃣ UI updates
            toast.success(
                `Category "${newCategoryName}" created. Hadith saved to it!`
            );

            setNewCategoryName('');
            setShowCategoryModal(false);

        } catch (error) {
            console.error("Category creation failed:", error);
            toast.error(
                error.response?.data?.message || "Failed to create category"
            );
        }
    };

    const handleSaveToExistingCategory = (categoryId) => {
        if (!activeCategoryBook) return;
        addItemToCategory(categoryId, {
            id: `book-${activeCategoryBook.id}`,
            type: 'book',
            ref: activeCategoryBook.title,
            content: activeCategoryBook.author
        });
        toast.success('Book saved to category');
        setShowCategoryModal(false);
        setActiveCategoryBook(null);
    };

    const openAddBookModal = () => {
        setIsEditing(false);
        setEditingBookId(null);
        setNewBook({
            title: '',
            category: 'quran',
            author: '',
            description: '',
            link: '',
            cover: '',
            is_local: false,
            format: 'External'
        });
        setShowAddBookModal(true);
    };

    const openEditBookModal = (book) => {
        setIsEditing(true);
        setEditingBookId(book.id);
        setNewBook({
            title: book.title,
            category: book.category,
            author: book.author,
            description: book.description,
            link: book.link,
            cover: book.cover,
            is_local: book.is_local || false,
            format: book.format || 'External'
        });
        setShowAddBookModal(true);
    };

    function handleFileUpload(e) {
        const file = e.target.files[0];
        if (!file) return;

        // ✅ Validate file size
        if (file.size > MAX_FILE_SIZE) {
            toast.error('File size must be 50MB or less');
            e.target.value = ''; // reset input
            return;
        }

        // (optional) validate file type
        const allowedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/epub+zip'
        ];

        if (!allowedTypes.includes(file.type)) {
            toast.error('Invalid file type');
            e.target.value = '';
            return;
        }

        setNewBook(prev => ({
            ...prev,
            file,
            is_local: true,
            format: file.name.split('.').pop().toUpperCase()
        }));

        toast.success('File selected');
    }

    const handleAddBookSubmit = async () => {
        if (!newBook.title || !newBook.author) {
            toast.error('Title and Author are required');
            return;
        }

        const formData = new FormData();
        formData.append('title', newBook.title);
        formData.append('author', newBook.author);
        formData.append('category', newBook.category);
        formData.append('description', newBook.description || '');
        formData.append('cover', newBook.cover || '');
        formData.append('link', newBook.link || '');
        formData.append('is_local', newBook.is_local ? 1 : 0);
        formData.append('format', newBook.format || 'External');

        if (newBook.file instanceof File) {
            formData.append('file', newBook.file);
        }

        try {
            setIsSaving(true); // 🔥 start loading

            if (isEditing) {
                await updateCustomBook(editingBookId, formData);
                toast.success('Book updated');
            } else {
                await addCustomBook(formData);
                toast.success('Book added successfully');
            }

            await fetchBooks();
            setShowAddBookModal(false);
        } catch (err) {
            console.error(err);
            toast.error(
                err.response?.data?.message || 'Failed to save book'
            );
        } finally {
            setIsSaving(false); // 🔥 stop loading (always)
        }
    };

    const handleDeleteBook = (book) => {
        if (window.confirm(`Are you sure you want to delete "${book.title}"?`)) {
            deleteBook(book.id, book.isCustom);
            if (viewingBook?.id === book.id) setViewingBook(null); // Go back if deleting viewed book
            toast.success("Book deleted");
        }
    };

    const BACKEND_URL = import.meta.env.VITE_API_URL;

    return (
        <div className="min-h-screen pt-8 bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto mb-6">

                {!user && (
                    <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg text-sm text-emerald-700 dark:text-emerald-300">
                        You’re browsing as a guest. Sign in to save favorites, notes, and categories.
                    </div>
                )}

                {/* Single Book View */}
                {viewingBook ? (
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 md:p-8 relative"
                    >
                        <button
                            onClick={() => setViewingBook(null)}
                            className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 mb-6 transition-colors"
                        >
                            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
                            <span className="font-medium">Back to Library</span>
                        </button>

                        <div className="flex flex-col md:flex-row gap-8">
                            {/* Cover Image */}
                            <div className="w-full md:w-1/3 max-w-sm mx-auto md:mx-0">
                                <div className="aspect-[3/4] relative rounded-lg overflow-hidden shadow-lg">
                                    <img
                                        src={viewingBook.cover || 'https://via.placeholder.com/300x400?text=No+Cover'}
                                        alt={viewingBook.title}
                                        className="w-full h-full object-cover"
                                    />
                                    {viewingBook.format && (
                                        <span className={`absolute top-4 left-4 px-2 py-1 text-xs uppercase font-bold tracking-wider rounded shadow-sm ${viewingBook.is_local
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                            }`}>
                                            {viewingBook.format}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Details */}
                            <div className="flex-1 space-y-6">
                                <div>
                                    <span className="inline-block px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 rounded-full text-xs font-semibold uppercase tracking-wide mb-3">
                                        {categories.find(c => c.id === viewingBook.category)?.name || viewingBook.category}
                                    </span>
                                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 leading-tight">
                                        {viewingBook.title}
                                    </h1>
                                    <p className="text-xl text-gray-600 dark:text-gray-300 font-medium">
                                        {viewingBook.author}
                                    </p>
                                </div>

                                <div className="prose dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 leading-relaxed">
                                    <p>{viewingBook.description || "No description available."}</p>
                                </div>

                                <div className="flex flex-wrap gap-4 pt-4">
                                    <a
                                        href={
                                            viewingBook.is_local
                                            ? `${BACKEND_URL}/${viewingBook.link}`
                                            : viewingBook.link
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold shadow-sm"
                                    >
                                        Review Book
                                    </a>

                                    {/* Action Toolbar */}
                                    <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700/50 p-1.5 rounded-lg">
                                        {isAdmin && (
                                            <button
                                                onClick={() => openEditBookModal(viewingBook)}
                                                className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-white dark:hover:bg-gray-600 rounded transition-colors"
                                                title="Edit Book"
                                            >
                                                <SafeIcon icon={FiEdit} className="w-5 h-5" />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleFavorite(viewingBook)}
                                            className="p-2 text-gray-500 hover:text-red-500 hover:bg-white dark:hover:bg-gray-600 rounded transition-colors"
                                            title="Add to Favorites"
                                        >
                                            <SafeIcon icon={FiHeart} className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => setActiveNoteBook(viewingBook)}
                                            className="p-2 text-gray-500 hover:text-blue-500 hover:bg-white dark:hover:bg-gray-600 rounded transition-colors"
                                            title="Add Note"
                                        >
                                            <SafeIcon icon={FiEdit3} className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => { setActiveCategoryBook(viewingBook); setShowCategoryModal(true); }}
                                            className="p-2 text-gray-500 hover:text-orange-500 hover:bg-white dark:hover:bg-gray-600 rounded transition-colors"
                                            title="Add to Category"
                                        >
                                            <SafeIcon icon={FiFolder} className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => handleCopy(viewingBook)}
                                            className="p-2 text-gray-500 hover:text-emerald-500 hover:bg-white dark:hover:bg-gray-600 rounded transition-colors"
                                            title="Copy Info"
                                        >
                                            <SafeIcon icon={FiCopy} className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => handleShare(viewingBook)}
                                            className="p-2 text-gray-500 hover:text-purple-500 hover:bg-white dark:hover:bg-gray-600 rounded transition-colors"
                                            title="Share"
                                        >
                                            <SafeIcon icon={FiShare2} className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ) : (
                    // Default List View
                    <>
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8"
                        >
                            <div className="text-left">
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                                    Islamic Library
                                </h1>
                                <p className="text-sm text-gray-600 dark:text-gray-300 max-w-xl">
                                    Access a comprehensive collection of Islamic books, literature, and scholarly works.
                                </p>
                            </div>

                            <div className="flex items-center gap-2 w-full md:w-auto">
                                <div className="relative flex-1 md:w-64">
                                    <SafeIcon
                                        icon={FiSearch}
                                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Search books..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-gray-100 text-sm"
                                    />
                                </div>

                                <select
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    className="px-3 py-2 w-full sm:w-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                                >
                                    <option value="all">All Categories</option>
                                    {user?.categories?.length > 0 && (
                                        <optgroup label="My Categories">
                                            {user.categories
                                                .filter(cat => cat.items.some(item => item.item_type === "book"))
                                                .map(cat => (
                                                    <option key={cat.id} value={cat.id}>
                                                        {cat.name} ({cat.items.length})
                                                    </option>
                                                ))}
                                        </optgroup>
                                    )}
                                </select>

                                {isAdmin && (
                                    <button
                                        onClick={openAddBookModal}
                                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm whitespace-nowrap"
                                    >
                                        <SafeIcon icon={FiPlus} className="w-4 h-4" />
                                        <span>Add Book</span>
                                    </button>
                                )}
                            </div>
                        </motion.div>

                        <div className="grid lg:grid-cols-12 gap-8">
                            <div className="lg:col-span-2">
                                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 sticky top-4">
                                    <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                                        <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                                            Categories
                                        </h3>
                                    </div>

                                    <div className="p-2 space-y-1">
                                        {categories.map((category) => (
                                            <button
                                                key={category.id}
                                                onClick={() => setSelectedCategory(category.id)}
                                                className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm ${selectedCategory === category.id
                                                        ? 'bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200'
                                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className="truncate">{category.name}</span>
                                                    <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full">
                                                        {getCategoryCount(category.id)}
                                                    </span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="lg:col-span-10">
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                    {filteredBooks.map((book, index) => (
                                        <motion.div
                                            key={book.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow relative flex flex-col"
                                        >
                                            {isAdmin && (
                                                <button
                                                    onClick={() => handleDeleteBook(book)}
                                                    className="absolute top-1 right-1 z-10 bg-white/90 dark:bg-gray-800/90 p-1 rounded-full text-gray-500 hover:text-red-500 transition-colors shadow-sm"
                                                    title="Delete Book"
                                                >
                                                    <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                                                </button>
                                            )}

                                            <div
                                                className="h-48 w-full bg-gray-100 dark:bg-gray-700 relative group overflow-hidden cursor-pointer"
                                                onClick={() => setViewingBook(book)}
                                            >
                                                <img
                                                    src={book.cover || 'https://via.placeholder.com/300x400?text=No+Cover'}
                                                    alt={book.title}
                                                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                                />
                                                <div className="absolute top-2 left-2">
                                                    {book.format && (
                                                        <span className={`px-1.5 py-0.5 text-[10px] uppercase font-bold tracking-wider rounded shadow-sm ${book.is_local
                                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                                            }`}>
                                                            {book.format}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="p-3 flex flex-col flex-grow">
                                                <div className="mb-1">
                                                    <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">
                                                        {categories.find(c => c.id === book.category)?.name || book.category}
                                                    </span>
                                                </div>

                                                <h3
                                                    className="font-semibold text-gray-900 dark:text-white text-sm mb-1 line-clamp-2 leading-tight cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400"
                                                    title={book.title}
                                                    onClick={() => setViewingBook(book)}
                                                >
                                                    {book.title}
                                                </h3>

                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 truncate">
                                                    {book.author}
                                                </p>

                                                <div className="mt-auto pt-2">
                                                    <a
                                                        href={book.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center justify-center w-full py-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 text-xs rounded transition-colors font-medium"
                                                    >
                                                        <SafeIcon icon={FiExternalLink} className="w-3 h-3 mr-1" />
                                                        Review Book
                                                    </a>
                                                </div>
                                            </div>

                                            <div className="px-2 py-2 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between gap-1">
                                                {isAdmin && (
                                                    <button
                                                        onClick={() => openEditBookModal(book)}
                                                        className="text-gray-400 hover:text-emerald-600 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                                                        title="Edit"
                                                    >
                                                        <SafeIcon icon={FiEdit} className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleFavorite(book)}
                                                    className="text-gray-400 hover:text-red-500 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    title="Favorite"
                                                >
                                                    <SafeIcon icon={FiHeart} className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => setActiveNoteBook(book)}
                                                    className="text-gray-400 hover:text-blue-500 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    title="Note"
                                                >
                                                    <SafeIcon icon={FiEdit3} className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => { setActiveCategoryBook(book); setShowCategoryModal(true); }}
                                                    className="text-gray-400 hover:text-orange-500 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    title="Category"
                                                >
                                                    <SafeIcon icon={FiFolder} className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => handleCopy(book)}
                                                    className="text-gray-400 hover:text-emerald-500 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    title="Copy"
                                                >
                                                    <SafeIcon icon={FiCopy} className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => handleShare(book)}
                                                    className="text-gray-400 hover:text-purple-500 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    title="Share"
                                                >
                                                    <SafeIcon icon={FiShare2} className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                {filteredBooks.length === 0 && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="text-center py-12"
                                    >
                                        <SafeIcon icon={FiLibrary} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                                            No books found
                                        </h3>
                                        <p className="text-gray-600 dark:text-gray-300">
                                            Try adjusting your search or category filter.
                                        </p>
                                    </motion.div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Note Modal */}
            <AnimatePresence>
                {activeNoteBook && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6"
                        >
                            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Add Note</h3>
                            <p className="text-sm text-gray-500 mb-4">{activeNoteBook.title}</p>
                            <textarea
                                value={noteContent}
                                onChange={(e) => setNoteContent(e.target.value)}
                                className="w-full h-32 p-3 border rounded-lg mb-4 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                placeholder="Write your reflections..."
                            />
                            <div className="flex justify-end gap-2">
                                <button onClick={() => setActiveNoteBook(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
                                <button onClick={handleSaveNote} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Save Note</button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Category Modal */}
            <AnimatePresence>
                {showCategoryModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-6"
                        >
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Save to Category</h3>
                                <button onClick={() => { setShowCategoryModal(false); setActiveCategoryBook(null); }}><SafeIcon icon={FiX} className="text-gray-500" /></button>
                            </div>

                            {user?.categories && user.categories.length > 0 && (
                                <div className="mb-4 space-y-2 max-h-40 overflow-y-auto">
                                    <p className="text-xs text-gray-500 font-semibold uppercase">Existing Categories</p>
                                    {user.categories
                                    .filter(cat => cat.items.some(item => item.item_type === "book"))
                                    .map(cat => (
                                        <button
                                        key={cat.id}
                                        onClick={() => handleSaveToExistingCategory(cat.id)}
                                        className="w-full text-left px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-sm flex justify-between"
                                        >
                                        <span className="text-gray-800 dark:text-gray-200">+ {cat.name}</span>
                                        <span className="text-gray-400 text-xs">{cat.items.length} items</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                <p className="text-xs text-gray-500 font-semibold uppercase mb-2">Create New Category</p>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newCategoryName}
                                        onChange={(e) => setNewCategoryName(e.target.value)}
                                        maxLength={50}
                                        placeholder="Name (max 15 chars)"
                                        className="flex-1 px-3 py-2 border rounded-lg text-sm bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    />
                                    <button
                                        onClick={handleAddToCategory}
                                        disabled={!newCategoryName.trim()}
                                        className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                                    >
                                        <SafeIcon icon={FiCheck} />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Add/Edit Book Modal */}
            <AnimatePresence>
                {showAddBookModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
                        >
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {isEditing ? 'Edit Book' : 'Add New Book'}
                                </h3>
                                <button onClick={() => setShowAddBookModal(false)}><SafeIcon icon={FiX} className="text-gray-500" /></button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Book Name *</label>
                                    <input
                                        type="text"
                                        value={newBook.title}
                                        onChange={e => setNewBook({ ...newBook, title: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        placeholder="e.g. Stories of the Prophets"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Author *</label>
                                    <input
                                        type="text"
                                        value={newBook.author}
                                        onChange={e => setNewBook({ ...newBook, author: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        placeholder="Author Name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                                    <select
                                        value={newBook.category}
                                        onChange={e => setNewBook({ ...newBook, category: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    >
                                        {categories.filter(c => c.id !== 'all').map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                                    <textarea
                                        value={newBook.description}
                                        onChange={e => setNewBook({ ...newBook, description: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        placeholder="Brief summary..."
                                        rows={3}
                                    />
                                </div>

                                {/* File Upload Section */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Upload Book (PDF)
                                    </label>

                                    <div className="flex items-center gap-2">
                                        <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors w-full">
                                            <SafeIcon icon={FiUpload} className="text-gray-500" />

                                            {/* ✅ Dynamic text */}
                                            <span className="text-sm text-gray-600 dark:text-gray-300 truncate">
                                                {newBook.file
                                                    ? `${newBook.file.name} (${(newBook.file.size / 1024 / 1024).toFixed(1)} MB)`
                                                    : 'Choose file...'}
                                            </span>

                                            <input
                                                type="file"
                                                className="hidden"
                                                accept=".pdf"
                                                onChange={handleFileUpload}
                                            />
                                        </label>
                                    </div>

                                    {/* Helper text */}
                                    <p className="text-xs text-gray-500 mt-1">
                                        PDF only · Max size 50MB
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Link (URL) / External Link</label>
                                    <input
                                        type="url"
                                        value={newBook.link}
                                        onChange={e => setNewBook({ ...newBook, link: e.target.value, is_local: false })}
                                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        placeholder="https://..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cover Image URL</label>
                                    <input
                                        type="url"
                                        value={newBook.cover}
                                        onChange={e => setNewBook({ ...newBook, cover: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        placeholder="https://..."
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-8">
                                <button
                                    onClick={() => setShowAddBookModal(false)}
                                    disabled={isSaving}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddBookSubmit}
                                    disabled={isSaving}
                                    className="px-6 py-2 bg-emerald-600 text-white rounded-lg
                                            hover:bg-emerald-700 font-medium
                                            disabled:opacity-60 disabled:cursor-not-allowed
                                            flex items-center gap-2"
                                >
                                    {isSaving && (
                                        <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                                    )}
                                    {isEditing
                                        ? isSaving ? 'Updating...' : 'Update Book'
                                        : isSaving ? 'Saving...' : 'Save Book'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <FooterLayout />
        </div>
    );
};

export default Books;